import {
  APP_CONFIG,
  ROUTE_MOBILE_NUMBER,
  ROUTE_SETTINGS_CONTACT_DETAILS,
} from '@1bill-app/constants';
import { objDeepEquals } from '@1bill-app/helpers/object.helper';
import { AuthService } from '@1bill-app/services/auth/state/auth.service';
import { HomeService } from '@1bill-app/services/home/home.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import {
  Settings,
  SettingsOptions,
  SettingsStore,
} from '@1bill-app/services/settings/settings.store';
import {
  CustomValidationService,
  MAX_DATE,
  MAX_DOB,
  MIN_DATE,
  MIN_DOB,
  safariDateToStandardDate,
  standardDateToSafariDate,
  validateDOB,
} from '@1bill-app/services/validation/custom-validation.service';
import {
  getCountryPhoneNumber,
  PhoneValidator,
} from '@1bill-app/services/validation/phone.validator';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Facebook } from '@awesome-cordova-plugins/facebook/ngx';
import { AlertController, LoadingController, NavController, Platform, ToastController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';

export enum AddressTypes {
  CURRENT = 'CURRENT',
  PRIOR = 'PRIOR',
  MAILING = 'MAILING',
}

@Component({
  selector: 'app-contact-details',
  templateUrl: './contact-details.page.html',
  styleUrls: ['./contact-details.page.scss'],
})
export class ContactDetailsPage implements OnInit, OnDestroy {
  constructor(
    public platform: Platform,
    private _cusValidation: CustomValidationService,
    private _settings: SettingsService,
    public query: SettingsQuery,
    private toastCtrl: ToastController,
    private loadingController: LoadingController,
    private homeService: HomeService,
    private logger: NGXLogger,
    private navCtrl: NavController,
    private authService: AuthService,
    private route: ActivatedRoute,
    private fb: Facebook,
    private store: SettingsStore,
    private alertController: AlertController,
  ) {
    this.isSafariDesktop = this._cusValidation.isSafariDesktop();
    this.initForm();
  }

  contactDetailsForm: FormGroup;
  userDetails: any;
  isFormUntouched: boolean = true;
  loading: any;
  saving: boolean = false;
  isSafariDesktop: boolean;
  checkError: any;
  checkWarning: any;
  mobileVerified$ = this.authService.checkIfMobileVerified();
  public _source: string;
  minDate = MIN_DATE;
  maxDate = MAX_DATE;
  minDob = MIN_DOB;
  maxDob = MAX_DOB;
  datePlaceholder = 'dd/mm/yyyy';

  searchingAddressPlace = false;
  // searchingAddressPlacePrior = false;
  addressPlaceId: string;
  addressPriorPlaceId: string;
  showHasPriorAddress = false;
  addressTypes = AddressTypes;

  subs = new SubSink();

  ngOnInit() {
    this._source = this.route.snapshot.queryParamMap.get('_source');
    this.fetchData();
    this.query.select().subscribe({
      next: (settingsData) => {
        // this.logger.debug('query fetch settings data:', settingsData)
        this.userDetails = settingsData;

        if(settingsData.mobileNumber && settingsData.mobileVerified){
          this.contactDetailsForm.controls['mobileNumber'].disable();
        }

        let initialFormValues = {
          lastName: this.userDetails.lastName,
          firstName: this.userDetails.firstName,
          email: this.userDetails.email,
          // oneBillEmail: this.userDetails.billEmail,
          mobileNumber: this.userDetails.mobileNumber,
          dateOfBirth: this.isSafariDesktop
            ? standardDateToSafariDate(this.userDetails.dateOfBirth)
            : this.userDetails.dateOfBirth,
          addressFull: this.userDetails.address,
        };
        this.contactDetailsForm.setValue(initialFormValues);
        // initialFormValues = initialFormValues = {
        //   ...initialFormValues, ...{...solveBrowserCompatibility(this.userDetails, this.isSafariDesktop)}};

        this.contactDetailsForm.valueChanges.subscribe((result) => {
          // this.logger.log('form change results:', result);
          if (objDeepEquals(initialFormValues, result)) {
            this.isFormUntouched = true;
          } else {
            // this.logger.log('value changed');
            this.isFormUntouched = false;
          }
        });
      },
      error: (err) => {
        this.logger.error('unable to retrieve user setting from data store', err);
      },
    });
  }

  async ionViewWillLeave() {
    if (this.loading) {
      this.loading.dismiss();
    }
  }

  async fetchData() {
    this.loading = await this.loadingController.create({
      message: 'Loading contact data...',
    });
    this.loading.present();
    this.subs.sink = this._settings.fetch().subscribe({
      next: (r) => {
        // this.logger.log('user settings data fetched:', JSON.stringify(r));
        this.isFormUntouched = true;
        this.loading.dismiss();
      },
      error: (e) => {
        this.logger.error('unable to fetch user settings data', e);
        this.loading.dismiss();
      },
    });
  }

  displayVerifyButton() {
    const mobile = getCountryPhoneNumber(this.contactDetailsForm.get('mobileNumber').value);

    return mobile ? true : false;
  }

  async presentMobileVerifiedAlert() {
    const alert = await this.alertController.create({
      cssClass: 'bill-alert',
      header: 'Mobile verified',
      message: 'Your mobile has already been verified. If you want to update your mobile number, please contact 1Bill support.',
      mode: 'ios',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            this.logger.log('Confirm Cancel: blah');
          },
        },
        {
          text: 'Ok',
          handler: () => {
            
          },
        },
      ],
    });
    await alert.present();
  }

  verifyMobileNow() {
    const mobile = getCountryPhoneNumber(this.contactDetailsForm.get('mobileNumber').value);
    this.navCtrl.navigateForward(ROUTE_MOBILE_NUMBER, {
      queryParams: { mobile, _source: ROUTE_SETTINGS_CONTACT_DETAILS },
    });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  initForm() {
    this.isFormUntouched = true;
    this.contactDetailsForm = new FormGroup({
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.email,
        Validators.required,
        // Validators.pattern('[a-zA-Z0-9._-]{2,}@[a-zA-Z]{2,}([.]{1}[a-zA-Z]{2,}|[.]{1}[a-zA-Z]{2,}[.]{1}[a-zA-Z]{2,})'),
      ]),
      // oneBillEmail: new FormControl({value: ''}, [
      //   // Validators.email,
      //   // Validators.required,
      //   // Validators.pattern('[a-zA-Z0-9._-]{2,}@[a-zA-Z]{2,}([.]{1}[a-zA-Z]{2,}|[.]{1}[a-zA-Z]{2,}[.]{1}[a-zA-Z]{2,})'),
      // ]),
      mobileNumber: new FormControl('', [PhoneValidator.validCountryPhone('AU')]),
      dateOfBirth: new FormControl('', [
        // Validators.required,
        validateDOB(this.platform),
      ]),
      addressFull: new FormControl('', [Validators.required]),
    });
  }

  showAddressSearch(type: AddressTypes) {
    if (type === AddressTypes.CURRENT) {
      this.searchingAddressPlace = true;
    }

    // if (type === AddressTypes.PRIOR) {
    //   this.searchingAddressPlacePrior = true;
    // }
  }

  get addressFull() {
    return this.contactDetailsForm.get('addressFull');
  }

  /**
   * Sets address item into form.
   */
  setAddress(
    item: { success: boolean; data: { desc: string; placeId: string } },
    type: AddressTypes,
  ) {
    this.logger.debug('setAddress args: ', { item, type });
    if (type === AddressTypes.CURRENT) {
      this.searchingAddressPlace = false;
      if (item.success) {
        this.contactDetailsForm.patchValue({ addressFull: item.data.desc });
        this.addressPlaceId = item.data.placeId;
      }
    }
    // if (type === AddressTypes.PRIOR) {
    //   this.searchingAddressPlacePrior = false;
    //   if (item.success) {
    //     this.contactDetailsForm.patchValue({ addressFullPrior: item.data.desc });
    //     this.addressPriorPlaceId = item.data.placeId;
    //   }
    // }
  }

  async onSave() {
    this.loading = await this.loadingController.create({
      message: 'Saving contact data...',
    });
    this.loading.present();
    try {
      this.logger.log('contactDetailsForm.value:', this.contactDetailsForm.value);

      const formData = {
        ...this.contactDetailsForm.value,
      };

      if (this.isSafariDesktop) {
        formData.dateOfBirth = safariDateToStandardDate(formData.dateOfBirth);
      }

      // STAGE: ready for save
      this.logger.debug('formData:', formData);
      this.saving = true;
      const data: Settings = formData;
      // data.addressJson.placeId = this.addressPlaceId; // assign new adress
      const payload: SettingsOptions = { option: 'CONTACT', settingsData: data };

      const response = await this._settings.saveContactDetails(payload).toPromise();
      this.logger.log({ response });

      if (response) {
        this.showSuccess('Settings saved successfully.');
        this.fetchData();
        this.isFormUntouched = true;
        this.homeService.triggerFetchHomeData('ContactDetailsPage::onSave()', true);
      } else {
        this.query.error$.subscribe((res) => {
          this.showError(res);
        });
      }
      this.loading.dismiss();
      // track Facebook SDK event for native apps - Update contact
      this.fb.logEvent('Contact');
    } catch (err) {
      this.logger.error('saveContactDetails', err);
      // display error toast
      this.showError('Error occurred while saving settings data!!');
      this.loading.dismiss();
    }
    this.saving = false;
  }

  async showSuccess(message: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: APP_CONFIG.TOAST_SUCCESS_DURATION,
      cssClass: ['toast-error'],
    });
    await toast.present();
  }

  async showError(err: any, danger = false) {
    if (!err) {
      return;
    }
    const message = err.statusText || err.message || err;
    const toast = await this.toastCtrl.create({
      message,
      duration: APP_CONFIG.TOAST_ERROR_DURATION,
      color: danger ? 'danger' : 'warning',
      // cssClass: ['toast-error']
    });
    await toast.present();
  }
}
